import React, { type FC } from "react";
import { useDivisionFilterSeed } from "@app/session-defaults/useDivisionFilterSeed";
import { BoType } from "@src/api/bo";
import { DivisionType } from "@src/api/dictionaries/divisions";
import { BusinessForm } from "@src/components/business-form";
import { BoStateSelect, DivisionSearchSelect, DivisionSectionSearchSelect } from "@src/components/business-data-entry";
import { Input } from "@shared/ui/input";
import { FilterFormPanel } from "@shared/ui/form-panel";
import type { StockGatheringFilter } from "../../../../../../api/stockGatherings";
import type { ListFilterPanelProps } from "../../../../../../shared/listFilterPanel";
import {
    stockGatheringCompletionFilterFormConfig,
    type StockGatheringCompletionFilterFormValues,
} from "./stockGatheringCompletionFilter.form-config";

/** Пропсы компонента {@link StockGatheringCompletionFilterForm}. */
export type StockGatheringCompletionFilterFormProps = ListFilterPanelProps<StockGatheringFilter>;

/** Форма фильтра КВ для комплектования. */
export const StockGatheringCompletionFilterForm: FC<StockGatheringCompletionFilterFormProps> = ({
    initialFilter,
    onApply,
}) => {
    const seed = useDivisionFilterSeed<StockGatheringCompletionFilterFormValues>("senderDivisionCode", {
        filter: { Types: [DivisionType.Склад] },
    });

    return (
        <FilterFormPanel
            initialFilter={initialFilter}
            seed={seed}
            submissionMemory={{
                key: "stock.gatheringCompletion.filter",
                schemaVersion: 2,
                fields: ["senderDivisionCode", "receiverDivisionCode", "receiverSectionCode", "stateCodes"],
            }}
            formProps={{ formConfig: stockGatheringCompletionFilterFormConfig }}
            toForm={(filter) => stockGatheringCompletionFilterFormConfig.toForm(filter)}
            toDomain={(values) => stockGatheringCompletionFilterFormConfig.toDomain(values)}
            onApply={onApply}
        >
            {(form) => {
                const receiverDivisionCode = form.watch("receiverDivisionCode");

                return (
                    <>
                        <BusinessForm.Field name="senderDivisionCode">
                            <DivisionSearchSelect
                                {...form
                                    .field("senderDivisionCode")
                                    .getSearchSelectProps((division) => division?.Code ?? null)}
                                flt={{ Types: [DivisionType.Склад] }}
                                fullscreenTitle="Выберите Склад"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="num">
                            <Input {...form.field("num").getPropsWithLabel()} />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="receiverDivisionCode">
                            <DivisionSearchSelect
                                {...form
                                    .field("receiverDivisionCode")
                                    .getSearchSelectProps((division) => division?.Code ?? null)}
                                onChange={(division) => {
                                    // TODO(forms/spp-lib): cascade-clear via form-config `dependsOn` (multi-field masters)
                                    const nextReceiverDivisionCode = division?.Code ?? null;

                                    form.setValue("receiverDivisionCode", nextReceiverDivisionCode);
                                    if (nextReceiverDivisionCode !== receiverDivisionCode) {
                                        form.setValue("receiverSectionCode", null);
                                    }
                                }}
                                fullscreenTitle="Выберите цех"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="receiverSectionCode">
                            <DivisionSectionSearchSelect
                                {...form
                                    .field("receiverSectionCode")
                                    .getSearchSelectProps((section) => section?.SectionCode ?? null)}
                                divisionCode={receiverDivisionCode}
                                fullscreenTitle="Выберите участок"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="stateCodes">
                            <BoStateSelect
                                {...form.field("stateCodes").getControlledPropsWithLabel()}
                                boType={BoType["Комплектовочная ведомость"]}
                                multiple
                                clearable
                                fullscreenTitle="Выберите статус(ы) КВ"
                            />
                        </BusinessForm.Field>
                    </>
                );
            }}
        </FilterFormPanel>
    );
};
